/* ==========================================================================
   TAG FAMILY MAPPING - edit this to reorganize which real Shopify tags
   belong to which family pill. This is the single source of truth: family
   pills, the secondary tag-chip row, and each All Writings row's colored
   dot are all generated from this object - nothing else needs editing to
   add/move/rename a tag or family.

   - `family`: label shown on the primary pill.
   - `tags`: real Shopify tag names (must match exactly, case-insensitive
     match is used when filtering/coloring) shown as chips once that
     family is selected.
   - `color`: CSS custom property (defined in assets/base.css) used for
     that family's pill accent and its All Writings row dot. Note:
     --color-surface-alt and --color-accent-deep exist elsewhere in the
     theme but are NOT used here - both are too dark to read as small
     text/dots against the near-black page background, so Craft & Ritual,
     Holidays & Seasons, and Occult use new, lighter colors instead
     (--color-sage/--color-amber/--color-wine, defined in base.css).
   - `icon`: matches a `data-icon` key on the hidden <template> elements
     in sections/blog-index.liquid (each one renders a real snippet,
     e.g. snippets/icon-eye.liquid) - shown above the pill on hover.
   ========================================================================== */
const TAG_FAMILIES = [
  {
    family: 'Shadow & Self',
    color: '--color-text-muted',
    icon: 'shadow-self',
    tags: ['Shadow Work', 'Spiritual', 'Healing', 'Energy', 'Meditation', 'Manifestation', 'Law of Attraction', 'Money']
  },
  {
    family: 'Astrology',
    color: '--color-base-accent',
    icon: 'astrology',
    tags: ['Astrology', 'Zodiac', 'Celestial', 'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']
  },
  {
    family: 'Moon & Sky',
    color: '--color-accent-light',
    icon: 'moon-sky',
    tags: ['Moon', 'Dreams', 'Sun']
  },
  {
    family: 'Craft & Ritual',
    color: '--color-sage',
    icon: 'craft-ritual',
    tags: ['Magic', 'Witch', 'Ritual', 'Crystals', 'Herbs', 'Tarot', 'Karma']
  },
  {
    family: 'Love & Dating',
    color: '--color-rose',
    icon: 'love-dating',
    tags: ['Love', 'Dating']
  },
  {
    family: 'Holidays & Seasons',
    color: '--color-amber',
    icon: 'holidays-seasons',
    tags: ['Holidays', 'Winter', 'Autumn']
  },
  {
    family: 'Occult',
    color: '--color-wine',
    icon: 'occult',
    tags: ['Death', 'Vampire', 'Omens']
  }
];
/* ========================================================================== */

class BlogIndex extends HTMLElement {
  connectedCallback() {
    this.initSeriesSparkles();

    this.familyPillsEl = this.querySelector('[data-family-pills]');
    this.subtagsEl = this.querySelector('[data-subtags]');
    this.subtagsLabelEl = this.querySelector('[data-subtags-label]');
    this.tagPillsEl = this.querySelector('[data-tag-pills]');
    if (!this.familyPillsEl || !this.subtagsEl || !this.subtagsLabelEl || !this.tagPillsEl) return;

    this.allCount = parseInt(this.dataset.allCount, 10) || 0;
    this.familyCounts = BlogIndex.parseFamilyCounts(this.dataset.familyCounts);
    this.pillIcons = this.readPillIcons();

    this.activeFamily = null;
    this.activeTag = null;

    const currentTag = this.dataset.currentTag || '';
    if (currentTag) {
      const match = BlogIndex.findFamilyForTagHandle(currentTag);
      if (match) {
        this.activeFamily = match.family;
        this.activeTag = currentTag;
      }
    }

    this.renderFamilyPills();
    if (this.activeFamily) this.renderTagPills(this.activeFamily);

    this.bindWritingsList();
  }

  static parseFamilyCounts(json) {
    try {
      return JSON.parse(json || '{}');
    } catch (error) {
      console.error('Failed to parse family counts:', error);
      return {};
    }
  }

  // Reads the server-rendered <template data-icon="..."> markup (see
  // sections/blog-index.liquid) into a { iconKey: svgMarkup } lookup, so
  // renderFamilyPills can drop each family's icon into its pill without
  // duplicating the SVG paths in JS.
  readPillIcons() {
    const icons = {};
    this.querySelectorAll('[data-pill-icons] template').forEach((template) => {
      icons[template.dataset.icon] = template.innerHTML;
    });
    return icons;
  }

  // ------------------------------------------------------------------
  // All Writings: fetch-based tag filtering + "Load Older Writings"
  // pagination. blog.articles is already scoped server-side by Shopify's
  // native tag routing (current_tags), so a tag-chip click just needs to
  // fetch the corresponding /tagged/x URL and swap this container's
  // content - no client-side filtering of pre-fetched data (246 articles
  // is too much to ship to every visitor up front).
  // ------------------------------------------------------------------
  bindWritingsList() {
    this.writingsContainer = this.querySelector('[data-writings-container]');
    this.loadMoreWrapper = this.querySelector('[data-load-more-wrapper]');
    if (!this.writingsContainer) return;

    this.applyRowTagsAndDots(this.writingsContainer);
    this.resetFeedAds();
    this.insertFeedAds(this.getWritingRows(this.writingsContainer));
    this.bindLoadMoreButton();

    this.addEventListener('blogindex:filter', (event) => {
      this.activeTag = event.detail.tag;
      this.fetchAndReplaceWritings(this.buildTagUrl(event.detail.tag));
    });
  }

  buildTagUrl(tagHandle) {
    const base = this.dataset.blogUrl || '';
    return tagHandle ? `${base}/tagged/${tagHandle}` : base;
  }

  // The link itself (see sections/blog-index.liquid) is a real
  // <a href="{{ paginate.next.url }}"> so the next page is a genuine,
  // crawlable link even without JS. This only intercepts the click to
  // swap in the smooth fetch/append behavior instead of a full page
  // navigation - the href stays as the no-JS/discovery fallback.
  bindLoadMoreButton() {
    if (!this.loadMoreWrapper) return;
    const link = this.loadMoreWrapper.querySelector('[data-load-more]');
    if (!link) return;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.loadMore(link);
    });
  }

  loadMore(link) {
    const url = link.getAttribute('href');
    if (!url || link.getAttribute('aria-disabled') === 'true') return;

    link.setAttribute('aria-disabled', 'true');
    link.classList.add('loading'); // matches the theme's existing .button.loading spinner styles

    this.fetchSectionDocument(url)
      .then((doc) => this.appendWritings(doc))
      .catch((error) => console.error('Failed to load more writings:', error))
      .finally(() => {
        link.removeAttribute('aria-disabled');
        link.classList.remove('loading');
      });
  }

  fetchAndReplaceWritings(url) {
    if (this.writingsContainer) this.writingsContainer.setAttribute('aria-busy', 'true');

    this.fetchSectionDocument(url)
      .then((doc) => {
        const newContainer = doc.querySelector('[data-writings-container]');
        const newLoadMoreWrapper = doc.querySelector('[data-load-more-wrapper]');

        if (newContainer && this.writingsContainer) {
          this.writingsContainer.innerHTML = newContainer.innerHTML;
          this.applyRowTagsAndDots(this.writingsContainer);
          // A filter swap is a brand-new list (page 1 of a different result
          // set), not a continuation of the previous one - restart the ad
          // cadence from scratch rather than carrying over the old count.
          this.resetFeedAds();
          this.insertFeedAds(this.getWritingRows(this.writingsContainer));
        }
        if (newLoadMoreWrapper && this.loadMoreWrapper) {
          this.loadMoreWrapper.innerHTML = newLoadMoreWrapper.innerHTML;
          this.bindLoadMoreButton();
        }
      })
      .catch((error) => console.error('Failed to filter writings:', error))
      .finally(() => {
        if (this.writingsContainer) this.writingsContainer.removeAttribute('aria-busy');
      });
  }

  appendWritings(doc) {
    const newContainer = doc.querySelector('[data-writings-container]');
    const newLoadMoreWrapper = doc.querySelector('[data-load-more-wrapper]');

    if (newContainer && this.writingsContainer) {
      // Capture just this batch's rows before merging - mergeMonthGroups
      // moves (not clones) these nodes into the live container, so the
      // references stay valid afterward. Feeding only the new rows into
      // insertFeedAds (rather than every row in the container) keeps the
      // running counter/interval continuing on from the previous batch
      // instead of restarting, and avoids re-scanning rows that already
      // had their chance to get an ad.
      const newRows = Array.from(newContainer.querySelectorAll('[data-writing-row]'));
      BlogIndex.mergeMonthGroups(this.writingsContainer, newContainer);
      this.applyRowTagsAndDots(this.writingsContainer);
      this.insertFeedAds(newRows);
    }

    if (this.loadMoreWrapper) {
      this.loadMoreWrapper.innerHTML = newLoadMoreWrapper ? newLoadMoreWrapper.innerHTML : '';
      this.bindLoadMoreButton();
    }
  }

  // ------------------------------------------------------------------
  // In-feed AdSense units, dropped into the All Writings list every 6-8
  // article rows. this.feedAdCounter/feedAdInterval persist on the
  // element across pagination batches (reset only on a full filter
  // replace - see fetchAndReplaceWritings) so the cadence is measured
  // against the TOTAL rows loaded so far, not restarted per batch, and a
  // batch boundary can't land two ads back-to-back.
  // ------------------------------------------------------------------
  resetFeedAds() {
    this.feedAdCounter = 0;
    this.feedAdInterval = BlogIndex.randomFeedAdInterval();
  }

  static randomFeedAdInterval() {
    return 6 + Math.floor(Math.random() * 3); // 6, 7, or 8
  }

  getWritingRows(container) {
    return Array.from(container.querySelectorAll('[data-writing-row]'));
  }

  insertFeedAds(rows) {
    rows.forEach((row) => {
      this.feedAdCounter++;
      if (this.feedAdCounter < this.feedAdInterval) return;

      row.insertAdjacentHTML('afterend', BlogIndex.feedAdMarkup());
      const adRow = row.nextElementSibling;
      const ins = adRow ? adRow.querySelector('ins.adsbygoogle') : null;

      // (adsbygoogle = window.adsbygoogle || []).push({}) must run once per
      // <ins> - a <script> tag inside insertAdjacentHTML's markup never
      // executes, so each dynamically-inserted slot is pushed here
      // explicitly instead (covers initial load, "Load Older Writings",
      // and tag-filter re-renders alike, since they all funnel through
      // this method). The push itself is gated on the card's stylesheet
      // having actually applied - see pushFeedAdWhenCssReady - so
      // Google's width measurement is consistent across refreshes
      // instead of racing the async CSS load.
      if (ins) BlogIndex.pushFeedAdWhenCssReady();

      this.feedAdCounter = 0;
      this.feedAdInterval = BlogIndex.randomFeedAdInterval();
    });
  }

  // section-blog-index.css (which sets .blog-index__ad-card's width,
  // padding and box-sizing) loads async via the media="print" onload=
  // "this.media='all'" swap in blog-index.liquid, so it can still be
  // in flight when this runs. If AdSense measures the <ins> before that
  // stylesheet has applied, it gets an inconsistent width - varying by
  // network/parse timing on each refresh - which is what caused the
  // intermittent ad cutoff (and, via the same async-CSS gap sitewide,
  // the FOUC fixed earlier). link.media flips from 'print' to 'all' the
  // instant the stylesheet's own load handler runs, so checking it here
  // is a direct, reliable signal rather than a fixed delay - the 'load'
  // listener only gets attached in the (rarer) case where the CSS
  // genuinely hasn't finished yet. The 2s fallback timeout exists only so
  // a renamed/missing asset can't strand an ad unpushed forever.
  static pushFeedAdWhenCssReady() {
    const push = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {}
    };

    const link = document.querySelector('link[href*="section-blog-index.css"]');
    if (!link || link.media === 'all') {
      push();
      return;
    }

    let done = false;
    const proceed = () => {
      if (done) return;
      done = true;
      push();
    };

    link.addEventListener('load', proceed, { once: true });
    setTimeout(proceed, 2000);
  }

  static feedAdMarkup() {
    return (
      '<li class="blog-index__writing-row blog-index__writing-row--ad" data-ad-slot-row>' +
      '<div class="blog-index__ad-card">' +
      '<p class="blog-index__ad-label caption-with-letter-spacing">Advertisement</p>' +
      '<ins class="adsbygoogle" ' +
      'style="display:block" ' +
      'data-ad-format="fluid" ' +
      'data-ad-layout-key="-fb+5w+4e-db+86" ' +
      'data-ad-client="ca-pub-7616968092347808" ' +
      'data-ad-slot="7911752405"></ins>' +
      '</div>' +
      '</li>'
    );
  }

  fetchSectionDocument(url) {
    const sectionId = this.dataset.sectionId;
    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = sectionId ? `${url}${separator}section_id=${sectionId}` : url;

    return fetch(fetchUrl)
      .then((response) => response.text())
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }

  // A freshly-appended batch (from "Load Older Writings") starts with its
  // own month-group header, generated independently by the server-side
  // Liquid loop for that page. If that first new group is the SAME month
  // as the last group already on the page (e.g. page 1 ends mid-October,
  // page 2 continues in October), merge its rows into the existing group
  // instead of rendering a second "October 2026" header back to back.
  static mergeMonthGroups(existingContainer, newContainer) {
    const existingGroups = existingContainer.querySelectorAll('[data-month-key]');
    const newGroups = newContainer.querySelectorAll('[data-month-key]');
    if (newGroups.length === 0) return;

    const lastExisting = existingGroups[existingGroups.length - 1];
    let startIndex = 0;

    if (lastExisting && lastExisting.dataset.monthKey === newGroups[0].dataset.monthKey) {
      const existingList = lastExisting.querySelector('[data-writings-list]');
      const newList = newGroups[0].querySelector('[data-writings-list]');
      if (existingList && newList) {
        Array.from(newList.children).forEach((row) => existingList.appendChild(row));
      }
      startIndex = 1;
    }

    for (let i = startIndex; i < newGroups.length; i++) {
      existingContainer.appendChild(newGroups[i]);
    }
  }

  // Fills in each row's displayed tag label + dot color after server
  // render (or after a fetch merge) - the family/color mapping only lives
  // in TAG_FAMILIES above, so this always runs client-side rather than
  // duplicating that mapping in Liquid.
  applyRowTagsAndDots(container) {
    container.querySelectorAll('[data-writing-row]').forEach((row) => {
      const tagsAttr = row.dataset.tags || '';
      const tags = tagsAttr.split('|').map((tag) => tag.trim()).filter(Boolean);

      if (tags.length > 0) {
        const resolved = BlogIndex.resolveRowTag(tags, this.activeTag);
        if (resolved) {
          // Set once on the row itself so the dot, tag label, AND the
          // hover sparkles all inherit the same --dot-color via CSS
          // custom property cascade - colored per-family, not a uniform
          // color for every row.
          row.style.setProperty('--dot-color', `var(${resolved.color})`);
          const label = row.querySelector('[data-writing-tag]');
          if (label) label.textContent = resolved.tag;
        }
      }

      // Randomize each row's sparkle positions once, same as the Shadow
      // Work Series cards above - this runs every time rows enter the DOM
      // (initial render, tag-filter fetch, load-more), not just once.
      row.querySelectorAll('[data-writing-sparkle]').forEach((sparkle, index) => {
        const baseDelay = 0.05 * ((index % 3) + 1);
        sparkle.style.cssText = BlogIndex.randomSparkleStyle(baseDelay);
      });
    });
  }

  // Picks which of an article's (possibly several) tags to display. If a
  // specific tag filter is active and the article carries that tag, show
  // it - that's the reason the row is in the filtered list. Otherwise
  // fall back to family priority order (the order families are defined
  // in TAG_FAMILIES above), so the shown tag is thematically meaningful
  // rather than whatever Shopify's alphabetically-sorted article.tags
  // happens to list first.
  static resolveRowTag(tags, activeTagHandle) {
    if (activeTagHandle) {
      const directMatch = tags.find((tag) => BlogIndex.handleize(tag) === activeTagHandle);
      if (directMatch) {
        const family = BlogIndex.findFamilyForTagHandle(activeTagHandle);
        if (family) return { tag: directMatch, color: family.color };
      }
    }

    for (const entry of TAG_FAMILIES) {
      const match = tags.find((tag) => entry.tags.some((familyTag) => BlogIndex.handleize(familyTag) === BlogIndex.handleize(tag)));
      if (match) return { tag: match, color: entry.color };
    }

    return null;
  }

  // Randomizes the Shadow Work Series card sparkles once per page load,
  // baked in as inline styles - same approach as the Featured Guide card
  // sparkles (assets/featured-guide.js), reused here for visual
  // consistency. CSS handles the actual fade-in/out on card :hover.
  initSeriesSparkles() {
    this.querySelectorAll('[data-series-sparkle]').forEach((sparkle, index) => {
      const baseDelay = 0.05 * ((index % 3) + 1);
      sparkle.style.cssText = BlogIndex.randomSparkleStyle(baseDelay);
    });
  }

  static randomSparkleStyle(baseDelaySeconds) {
    const topPercent = -15 + Math.random() * 130; // -15% to 115%
    const leftPercent = -10 + Math.random() * 120; // -10% to 110%
    const size = 4 + Math.random() * 2; // 4-6px
    const jitter = Math.random() * 0.1 - 0.05; // +/- 0.05s
    const transitionDelay = Math.max(0, baseDelaySeconds + jitter);
    const animationDelay = transitionDelay + 0.25;

    return [
      `top: ${topPercent.toFixed(1)}%`,
      `left: ${leftPercent.toFixed(1)}%`,
      `width: ${size.toFixed(1)}px`,
      `height: ${size.toFixed(1)}px`,
      `margin-top: -${(size / 2).toFixed(1)}px`,
      `margin-left: -${(size / 2).toFixed(1)}px`,
      `transition-delay: ${transitionDelay.toFixed(2)}s`,
      `animation-delay: ${animationDelay.toFixed(2)}s`
    ].join('; ');
  }

  renderFamilyPills() {
    // "All" deliberately has no hover icon - a generic sparkle/asterisk
    // would look near-identical to Astrology's star icon right next to
    // it, which reads as a mistake rather than two distinct symbols.
    const allPill = BlogIndex.pillMarkup('All', 'all', !this.activeFamily, null, null, null);
    const familyPills = TAG_FAMILIES.map((entry) =>
      BlogIndex.pillMarkup(entry.family, entry.family, entry.family === this.activeFamily, entry.color, this.familyCounts[entry.family], this.pillIcons[entry.icon])
    ).join('');

    this.familyPillsEl.innerHTML = allPill + familyPills;

    this.familyPillsEl.querySelectorAll('[data-family-pill]').forEach((pill) => {
      pill.addEventListener('click', () => this.onFamilyClick(pill.dataset.familyPill));
    });
  }

  onFamilyClick(familyKey) {
    if (familyKey === 'all') {
      this.activeFamily = null;
      this.activeTag = null;
      this.subtagsEl.hidden = true;
      this.tagPillsEl.innerHTML = '';
      this.renderFamilyPills();
      this.dispatchFilterChange(null);
      return;
    }

    this.activeFamily = familyKey;
    this.activeTag = null;
    this.renderFamilyPills();
    this.renderTagPills(familyKey);
  }

  renderTagPills(familyKey) {
    const entry = TAG_FAMILIES.find((f) => f.family === familyKey);
    if (!entry) return;

    this.subtagsLabelEl.textContent = entry.family;

    this.tagPillsEl.innerHTML = entry.tags
      .map((tag) => BlogIndex.subtagMarkup(tag, BlogIndex.handleize(tag), BlogIndex.handleize(tag) === this.activeTag, entry.color))
      .join('');
    this.subtagsEl.hidden = false;

    this.tagPillsEl.querySelectorAll('[data-family-pill]').forEach((pill) => {
      pill.addEventListener('click', () => this.onTagClick(pill.dataset.familyPill, entry));
    });
  }

  onTagClick(tagHandle, entry) {
    this.activeTag = tagHandle;
    this.tagPillsEl.querySelectorAll('[data-family-pill]').forEach((pill) => {
      pill.classList.toggle('is-active', pill.dataset.familyPill === tagHandle);
    });
    this.dispatchFilterChange(tagHandle, entry.color);
    this.scrollToWritings();
  }

  // Jumps down to the All Writings list (below the Shadow Work Series) so
  // the filtered results are actually visible without the user having to
  // scroll manually. Offset by the sticky header's real, current height
  // (measured live rather than hardcoded) so the list isn't left tucked
  // underneath it.
  scrollToWritings() {
    const target = this.querySelector('.blog-index__writings-section');
    if (!target) return;

    const stickyHeader = document.querySelector('sticky-header, header-wrapper');
    const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset - 24;

    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  }

  dispatchFilterChange(tagHandle, familyColor) {
    this.dispatchEvent(
      new CustomEvent('blogindex:filter', {
        bubbles: true,
        detail: { tag: tagHandle, familyColor: familyColor || null }
      })
    );
  }

  // Family pill - solid active-fill state uses the same --pill-color for
  // both background and (via CSS) a dark text color, since every family
  // color was checked against WCAG AA contrast for dark text specifically
  // (see the --color-wine comment in base.css for the one color that
  // needed adjusting to pass).
  static pillMarkup(label, key, isActive, color, count, iconSvg) {
    const style = color ? ` style="--pill-color: var(${color})"` : '';
    const countHtml = typeof count === 'number' ? ` <span class="blog-index__pill-count">${count}</span>` : '';
    const iconHtml = iconSvg ? `<span class="blog-index__pill-hover-icon" aria-hidden="true">${iconSvg}</span>` : '';
    return `<li><button type="button" class="blog-index__pill${isActive ? ' is-active' : ''}" data-family-pill="${key}"${style}>${iconHtml}${BlogIndex.escapeHtml(label)}${countHtml}</button></li>`;
  }

  // Sub-tag - plain underlined text link (not a pill), tinted (not solid)
  // in the parent family's color.
  static subtagMarkup(label, key, isActive, color) {
    const style = color ? ` style="--tag-color: var(${color})"` : '';
    return `<li><button type="button" class="blog-index__subtag-link${isActive ? ' is-active' : ''}" data-family-pill="${key}"${style}>${BlogIndex.escapeHtml(label)}</button></li>`;
  }

  static findFamilyForTagHandle(handle) {
    for (const entry of TAG_FAMILIES) {
      if (entry.tags.some((tag) => BlogIndex.handleize(tag) === handle)) return entry;
    }
    return null;
  }

  // Matches Shopify's own tag-to-URL handleization closely enough for our
  // fixed, known tag list (lowercase, spaces to hyphens) - this isn't a
  // general-purpose slugify, it only needs to work for the tags above.
  static handleize(value) {
    return value.toLowerCase().trim().replace(/\s+/g, '-');
  }

  static escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

customElements.define('blog-index', BlogIndex);
